// src/screens/BlogArticleScreen.jsx

import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import DOMPurify from "dompurify";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

import blogData from "../data/blogData.json";

/* ======================================================
   DISEÑO EDITORIAL
   Estilos encapsulados. No requiere fuentes externas.
====================================================== */

const EDITORIAL_STYLES = `
  .sc-editorial {
    --sc-paper: #F7F3E9;
    --sc-ink: #081B3E;
    --sc-blue: #1648EE;
    --sc-magenta: #F72585;
    --sc-muted: #536078;
    --sc-rule: rgba(8, 27, 62, .19);
    --sc-serif: Georgia, "Times New Roman", serif;
    --sc-sans: Arial, Helvetica, sans-serif;
    --sc-display: "Arial Narrow", "Roboto Condensed", Arial, sans-serif;
    --sc-space: clamp(2.5rem, 6vh, 5rem);
    --sc-hero-space: clamp(1.75rem, 5vh, 4rem);
    --sc-stack: clamp(1rem, 2.5vh, 1.75rem);
    --sc-gap: clamp(1.5rem, 3vw, 3rem);
    background: var(--sc-paper);
    color: var(--sc-ink);
    font-family: var(--sc-sans);
    padding-bottom: var(--sc-space);
    /* App ya compensa el navbar fijo: no se añade otro padding superior. */
  }

  .sc-editorial,
  .sc-editorial * {
    box-sizing: border-box;
  }

  .sc-editorial a {
    color: inherit;
  }

  .sc-editorial a:focus-visible {
    outline: 2px solid var(--sc-blue);
    outline-offset: 5px;
  }

  .sc-editorial .sc-ed-wrap {
    width: 100%;
    max-width: 75rem;
    padding-inline: clamp(1rem, 3vw, 2.5rem);
    margin-inline: auto;
  }

  .sc-editorial .sc-ed-breadcrumbs {
    padding-block: .25rem;
    font-size: .75rem;
    line-height: 1.5;
    color: rgba(247, 243, 233, .85);
  }

  .sc-editorial .sc-ed-breadcrumbs ol {
    display: flex;
    align-items: center;
    gap: .5rem;
    min-width: 0;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .sc-editorial .sc-ed-breadcrumbs li {
    flex-shrink: 0;
  }

  .sc-editorial .sc-ed-breadcrumbs li:last-child {
    flex: 1 1 0%;
    min-width: 0;
    overflow-wrap: anywhere;
  }

  .sc-editorial .sc-ed-breadcrumbs a {
    display: inline-flex;
    align-items: center;
    min-height: 2.75rem;
    text-decoration: none;
  }

  .sc-editorial .sc-ed-breadcrumbs a:hover {
    color: var(--sc-paper);
    text-decoration: underline;
    text-underline-offset: 4px;
  }

  .sc-editorial .sc-ed-masthead {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: .75rem 1.5rem;
    padding-block: 1rem;
    border-bottom: 1px solid var(--sc-rule);
  }

  .sc-editorial .sc-ed-publication {
    display: inline-flex;
    align-items: center;
    gap: .75rem;
    min-width: 0;
    min-height: 2.75rem;
    font-family: var(--sc-display);
    font-stretch: condensed;
    font-size: clamp(1.125rem, 2vw, 1.375rem);
    font-weight: 800;
    line-height: 1.2;
    letter-spacing: -.025em;
    color: var(--sc-blue);
    text-decoration: none;
    overflow-wrap: anywhere;
  }

  .sc-editorial .sc-ed-dot {
    width: .5rem;
    height: .5rem;
    border-radius: 50%;
    background: var(--sc-magenta);
    flex-shrink: 0;
  }

  .sc-editorial .sc-ed-edition {
    max-width: 18rem;
    margin: 0;
    color: var(--sc-muted);
    font-size: .6875rem;
    line-height: 1.7;
    letter-spacing: .12em;
    text-transform: uppercase;
    text-align: right;
    overflow-wrap: anywhere;
  }

  .sc-editorial .sc-ed-header {
    position: relative;
    max-width: 62rem;
    padding-block: var(--sc-hero-space);
  }

  .sc-editorial .sc-ed-kicker {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: .75rem;
    margin: 0 0 var(--sc-stack);
    color: var(--sc-blue);
    font-size: .75rem;
    font-weight: 700;
    line-height: 1.5;
    text-transform: uppercase;
    letter-spacing: .14em;
  }

  .sc-editorial .sc-ed-kicker::before {
    content: "";
    width: 34px;
    height: 3px;
    background: var(--sc-magenta);
  }

  .sc-editorial .sc-ed-title {
    margin: 0;
    color: var(--sc-ink);
    font-family: var(--sc-display);
    font-stretch: condensed;
    font-size: clamp(2rem, 5.5vw, 3.75rem);
    font-weight: 800;
    line-height: 1.07;
    letter-spacing: -.035em;
    overflow-wrap: anywhere;
    text-wrap: balance;
  }

  .sc-editorial .sc-ed-deck {
    max-width: 49rem;
    margin: var(--sc-stack) 0 0;
    color: var(--sc-muted);
    font-family: var(--sc-sans);
    font-size: clamp(1rem, 1.8vw, 1.1875rem);
    line-height: 1.65;
    overflow-wrap: anywhere;
  }

  .sc-editorial .sc-ed-meta {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    justify-content: space-between;
    gap: .875rem 1.5rem;
    margin-top: var(--sc-stack);
    padding-top: 1rem;
    border-top: 1px solid var(--sc-rule);
  }

  .sc-editorial .sc-ed-byline {
    min-width: 0;
    margin: 0;
    color: var(--sc-muted);
    font-size: .8125rem;
    line-height: 1.6;
    overflow-wrap: anywhere;
  }

  .sc-editorial .sc-ed-byline strong {
    display: block;
    font-size: .875rem;
    color: var(--sc-ink);
  }

  .sc-editorial .sc-ed-details {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: .5rem 1.125rem;
    min-width: 0;
    margin: 0;
    font-size: .8125rem;
    line-height: 1.6;
    color: var(--sc-muted);
    overflow-wrap: anywhere;
  }

  .sc-editorial .sc-ed-cover {
    max-width: 52rem;
    margin: var(--sc-gap) auto 0;
  }

  .sc-editorial .sc-ed-cover img {
    display: block;
    width: 100%;
    height: auto;
    max-height: min(55vh, 32rem);
    object-fit: contain;
  }

  .sc-editorial .sc-ed-cover figcaption {
    margin: .75rem 0 0;
    font-size: .75rem;
    line-height: 1.7;
    color: var(--sc-muted);
    overflow-wrap: anywhere;
  }

  .sc-editorial .sc-ed-layout {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: var(--sc-gap);
    max-width: 44rem;
    margin: var(--sc-space) auto 0;
  }

  .sc-editorial .sc-ed-reading {
    min-width: 0;
  }

  .sc-editorial .sc-ed-language-note {
    margin: 0 0 1.5rem;
    padding: 0 0 1rem;
    border-bottom: 1px solid var(--sc-rule);
    color: var(--sc-muted);
    font-family: var(--sc-sans);
    font-size: .8125rem;
    line-height: 1.7;
  }

  /* CUERPO DE LECTURA */

  .sc-editorial .sc-ed-prose {
    color: #253044;
    font-family: var(--sc-serif);
    font-size: clamp(1.125rem, 1.5vw, 1.25rem);
    line-height: 1.8;
    overflow-wrap: anywhere;
  }

  .sc-editorial .sc-ed-prose > div + div {
    margin-top: 1.5em;
  }

  .sc-editorial .sc-ed-prose > div > :first-child {
    margin-top: 0;
  }

  .sc-editorial .sc-ed-prose > div > :last-child {
    margin-bottom: 0;
  }

  .sc-editorial .sc-ed-prose p {
    margin: 0 0 1.35em;
  }

  /* Capitular solo cuando el artículo comienza con un párrafo. */
  .sc-editorial .sc-ed-prose > div:first-child > p:first-child::first-letter {
    float: left;
    margin: .08em .12em 0 0;
    color: var(--sc-blue);
    font-family: var(--sc-serif);
    font-size: 3.55em;
    line-height: .8;
  }

  .sc-editorial .sc-ed-prose h2,
  .sc-editorial .sc-ed-prose h3,
  .sc-editorial .sc-ed-prose h4,
  .sc-editorial .sc-ed-prose h5,
  .sc-editorial .sc-ed-prose h6 {
    color: var(--sc-ink);
    line-height: 1.25;
    letter-spacing: -.025em;
    text-wrap: balance;
  }

  .sc-editorial .sc-ed-prose h2 {
    margin: 1.6em 0 .65em;
    font-family: var(--sc-display);
    font-stretch: condensed;
    font-size: clamp(1.625rem, 2.6vw, 2rem);
    font-weight: 800;
  }

  .sc-editorial .sc-ed-prose h3 {
    margin: 1.65em 0 .7em;
    font-family: var(--sc-sans);
    font-size: 21px;
    font-weight: 700;
  }

  .sc-editorial .sc-ed-prose h4,
  .sc-editorial .sc-ed-prose h5,
  .sc-editorial .sc-ed-prose h6 {
    margin: 1.5em 0 .6em;
    font-family: var(--sc-sans);
    font-size: 18px;
    font-weight: 700;
  }

  .sc-editorial .sc-ed-prose strong,
  .sc-editorial .sc-ed-prose b {
    color: var(--sc-ink);
    font-weight: 700;
  }

  .sc-editorial .sc-ed-prose a {
    color: var(--sc-blue);
    text-decoration: underline;
    text-decoration-thickness: 1px;
    text-underline-offset: 4px;
    overflow-wrap: anywhere;
  }

  .sc-editorial .sc-ed-prose ul,
  .sc-editorial .sc-ed-prose ol {
    margin: 1.3em 0;
    padding-left: 1.4em;
  }

  .sc-editorial .sc-ed-prose ul { list-style: disc; }
  .sc-editorial .sc-ed-prose ol { list-style: decimal; }

  .sc-editorial .sc-ed-prose li {
    margin: .45em 0;
    padding-left: .15em;
  }

  .sc-editorial .sc-ed-prose li::marker {
    color: var(--sc-blue);
  }

  .sc-editorial .sc-ed-prose blockquote {
    margin: 1.6em 0;
    padding: 1em 0 1em 24px;
    border-left: 3px solid var(--sc-magenta);
    color: var(--sc-ink);
    font-size: 1.2em;
    font-style: italic;
    line-height: 1.55;
  }

  .sc-editorial .sc-ed-prose blockquote p:last-child {
    margin-bottom: 0;
  }

  .sc-editorial .sc-ed-prose figure {
    margin: 1.8em 0;
  }

  .sc-editorial .sc-ed-prose img {
    display: block;
    max-width: 100%;
    height: auto;
    margin: 1.5em auto;
  }

  .sc-editorial .sc-ed-prose figcaption {
    margin-top: 12px;
    color: var(--sc-muted);
    font-family: var(--sc-sans);
    font-size: 12px;
    line-height: 1.7;
  }

  .sc-editorial .sc-ed-prose table {
    display: block;
    width: 100%;
    max-width: 100%;
    overflow-x: auto;
    border-collapse: collapse;
    margin: 1.8em 0;
    font-family: var(--sc-sans);
    font-size: 14px;
    line-height: 1.7;
  }

  .sc-editorial .sc-ed-prose th,
  .sc-editorial .sc-ed-prose td {
    min-width: 120px;
    padding: 12px 14px;
    border-bottom: 1px solid var(--sc-rule);
    text-align: left;
    vertical-align: top;
  }

  .sc-editorial .sc-ed-prose th {
    color: var(--sc-ink);
    border-top: 2px solid var(--sc-blue);
    font-weight: 700;
  }

  .sc-editorial .sc-ed-prose pre {
    max-width: 100%;
    overflow-x: auto;
    padding: 20px;
    margin: 1.5em 0;
    background: #EBE8E0;
    font-size: 14px;
    line-height: 1.7;
  }

  .sc-editorial .sc-ed-prose code {
    font-family: ui-monospace, SFMono-Regular, Consolas, monospace;
    font-size: .88em;
  }

  .sc-editorial .sc-ed-prose :not(pre) > code {
    background: #EBE8E0;
    padding: .12em .3em;
  }

  .sc-editorial .sc-ed-prose hr {
    border: 0;
    border-top: 1px solid var(--sc-rule);
    margin: 2.3em 0;
  }

  .sc-editorial .sc-ed-signature {
    margin-top: 2rem;
    padding-top: 1.25rem;
    border-top: 1px solid var(--sc-rule);
    font-family: var(--sc-sans);
    font-size: .8125rem;
    line-height: 1.8;
    color: var(--sc-muted);
  }

  .sc-editorial .sc-ed-signature p { margin: 0; }
  .sc-editorial .sc-ed-signature strong { color: var(--sc-ink); }

  .sc-editorial .sc-ed-endmark {
    display: block;
    width: 9px;
    height: 9px;
    margin-top: 32px;
    background: var(--sc-magenta);
  }

  /* COLUMNA SECUNDARIA */

  .sc-editorial .sc-ed-aside {
    min-width: 0;
    padding-top: 22px;
    border-top: 1px solid var(--sc-rule);
  }

  .sc-editorial .sc-ed-aside-title {
    margin: 0 0 .5rem;
    color: var(--sc-muted);
    font-size: .75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .12em;
    line-height: 1.7;
  }

  .sc-editorial .sc-ed-share {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 22px;
  }

  .sc-editorial .sc-ed-share a {
    display: inline-flex;
    align-items: center;
    min-height: 44px;
    color: var(--sc-blue);
    font-size: 12px;
    font-weight: 700;
    text-decoration: none;
  }

  .sc-editorial .sc-ed-share a:hover {
    text-decoration: underline;
    text-underline-offset: 4px;
  }

  .sc-editorial .sc-ed-aside-language {
    margin: 1rem 0 0;
    color: var(--sc-muted);
    font-size: .75rem;
    line-height: 1.8;
  }

  /* NAVEGACIÓN INFERIOR */

  .sc-editorial .sc-ed-nav {
    max-width: 44rem;
    margin: var(--sc-space) auto 0;
    padding-block: 1.5rem;
    border-top: 2px solid var(--sc-blue);
    border-bottom: 1px solid var(--sc-rule);
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1.5rem;
  }

  .sc-editorial .sc-ed-nav-link {
    display: block;
    min-width: 0;
    min-height: 2.75rem;
    text-decoration: none;
  }

  .sc-editorial .sc-ed-nav-link.is-next {
    grid-column: 2;
    text-align: right;
  }

  .sc-editorial .sc-ed-nav-label {
    display: flex;
    align-items: center;
    gap: .375rem;
    color: var(--sc-blue);
    font-size: .6875rem;
    font-weight: 700;
    letter-spacing: .1em;
    text-transform: uppercase;
    line-height: 1.7;
  }

  .sc-editorial .sc-ed-nav-link.is-next .sc-ed-nav-label {
    justify-content: flex-end;
  }

  .sc-editorial .sc-ed-nav-title {
    margin: .75rem 0 0;
    font-family: var(--sc-display);
    font-size: clamp(1.125rem, 2vw, 1.375rem);
    font-weight: 700;
    line-height: 1.35;
    overflow-wrap: anywhere;
  }

  .sc-editorial .sc-ed-nav-link:hover .sc-ed-nav-title {
    color: var(--sc-blue);
  }

  .sc-editorial .sc-ed-back-wrap {
    max-width: 44rem;
    margin: 1rem auto 0;
  }

  .sc-editorial .sc-ed-back {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-height: 44px;
    color: var(--sc-blue) !important;
    font-size: 12px;
    font-weight: 700;
    text-decoration: none;
  }

  .sc-editorial .sc-ed-back:hover {
    text-decoration: underline;
    text-underline-offset: 4px;
  }

  .sc-editorial .sc-ed-missing {
    max-width: 48rem;
    margin: 0 auto;
    padding-block: var(--sc-space);
  }

  .sc-editorial .sc-ed-missing-code {
    margin: 0 0 20px;
    color: var(--sc-blue);
    font-size: 12px;
    letter-spacing: .2em;
  }

  .sc-editorial .sc-ed-missing p:not(.sc-ed-missing-code) {
    max-width: 560px;
    margin: 24px 0;
    color: var(--sc-muted);
    font-size: 16px;
    line-height: 1.8;
  }

  @media (min-width: 64rem) {
    .sc-editorial .sc-ed-layout {
      max-width: none;
      grid-template-columns: minmax(0, 1fr) minmax(0, 44rem) minmax(0, 1fr);
      column-gap: 1.5rem;
      align-items: start;
    }

    .sc-editorial .sc-ed-reading {
      grid-column: 2;
      grid-row: 1;
    }

    .sc-editorial .sc-ed-aside {
      grid-column: 1;
      grid-row: 1;
      position: sticky;
      top: calc(var(--solcont-navbar-height, 4rem) + 1.5rem);
      padding-top: 10px;
      border-top: 2px solid var(--sc-blue);
    }

    .sc-editorial .sc-ed-share {
      flex-direction: column;
      align-items: flex-start;
      gap: 0;
    }
  }

  @media (max-width: 600px) {
    .sc-editorial .sc-ed-edition { text-align: left; font-size: .625rem; }
    .sc-editorial .sc-ed-masthead { align-items: flex-start; gap: .25rem .75rem; }
    .sc-editorial .sc-ed-breadcrumbs ol { align-items: flex-start; }
    .sc-editorial .sc-ed-breadcrumbs li { padding-top: .8125rem; }
    .sc-editorial .sc-ed-breadcrumbs li:first-child,
    .sc-editorial .sc-ed-breadcrumbs li:nth-child(3) { padding-top: 0; }
    .sc-editorial .sc-ed-title { letter-spacing: -.035em; }

    .sc-editorial .sc-ed-nav {
      grid-template-columns: minmax(0, 1fr);
      gap: 26px;
    }

    .sc-editorial .sc-ed-nav-link.is-next {
      grid-column: auto;
      text-align: left;
    }

    .sc-editorial .sc-ed-nav-link.is-next .sc-ed-nav-label {
      justify-content: flex-start;
    }
  }

  @media print {
    .sc-editorial {
      background: white;
      border: 0;
      padding: 0;
    }

    .sc-editorial .sc-ed-wrap {
      width: 100%;
    }

    .sc-editorial .sc-ed-breadcrumbs,
    .sc-editorial .sc-ed-aside,
    .sc-editorial .sc-ed-nav,
    .sc-editorial .sc-ed-back-wrap {
      display: none;
    }

    .sc-editorial .sc-ed-layout {
      display: block;
      max-width: none;
    }

    .sc-editorial .sc-ed-title {
      font-size: 26pt;
    }

    .sc-editorial .sc-ed-prose {
      font-size: 12pt;
    }
  }

  /* Cabecera de marca; la retícula y los círculos son decorativos. */
  .sc-editorial .sc-ed-hero {
    position: relative;
    isolation: isolate;
    color: var(--sc-paper);
    background-color: var(--sc-blue);
    background-image:
      linear-gradient(rgba(255, 255, 255, .06) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255, 255, 255, .06) 1px, transparent 1px);
    background-size: 64px 64px;
    --sc-rule: rgba(247, 243, 233, .25);
  }

  .sc-editorial .sc-ed-hero-inner { position: relative; z-index: 1; }

  .sc-editorial .sc-ed-orbits {
    position: absolute;
    inset: 0;
    z-index: 0;
    overflow: hidden;
    pointer-events: none;
  }

  .sc-editorial .sc-ed-orbit {
    position: absolute;
    top: -8rem;
    right: -10rem;
    width: clamp(22rem, 40vw, 38rem);
    aspect-ratio: 1;
    border: 1px solid rgba(247, 243, 233, .18);
    border-radius: 50%;
  }

  .sc-editorial .sc-ed-orbit::before {
    content: "";
    position: absolute;
    inset: 18%;
    border: 1px solid rgba(247, 243, 233, .18);
    border-radius: 50%;
  }

  .sc-editorial .sc-ed-orbit::after {
    content: "";
    position: absolute;
    top: 14.65%;
    left: 14.65%;
    width: .875rem;
    height: .875rem;
    border-radius: 50%;
    background: var(--sc-magenta);
    transform: translate(-50%, -50%);
  }

  .sc-editorial .sc-ed-hero .sc-ed-title,
  .sc-editorial .sc-ed-hero .sc-ed-publication,
  .sc-editorial .sc-ed-hero .sc-ed-byline strong {
    color: var(--sc-paper);
  }

  .sc-editorial .sc-ed-hero .sc-ed-deck,
  .sc-editorial .sc-ed-hero .sc-ed-byline,
  .sc-editorial .sc-ed-hero .sc-ed-details,
  .sc-editorial .sc-ed-hero .sc-ed-edition,
  .sc-editorial .sc-ed-hero .sc-ed-kicker {
    color: rgba(247, 243, 233, .9);
  }

  .sc-editorial .sc-ed-hero a:focus-visible {
    outline-color: var(--sc-paper);
  }

  .sc-editorial .sc-ed-meta > *, .sc-ed-prose > * { min-width: 0; }
  .sc-editorial .sc-ed-nav-label svg, .sc-ed-back svg { flex-shrink: 0; }

  .sc-editorial .sc-ed-prose dl { margin: 1.3em 0; }
  .sc-editorial .sc-ed-prose dt { font-weight: 700; color: var(--sc-ink); }
  .sc-editorial .sc-ed-prose dd { margin: .4em 0 1em 1em; }
  .sc-editorial .sc-ed-prose table caption { text-align: left; padding-bottom: .75rem; }

  @supports (height: 100svh) {
    .sc-editorial {
      --sc-space: clamp(2.5rem, 6svh, 5rem);
      --sc-hero-space: clamp(1.75rem, 5svh, 4rem);
      --sc-stack: clamp(1rem, 2.5svh, 1.75rem);
    }
    .sc-editorial .sc-ed-cover img { max-height: min(55svh, 32rem); }
  }

  @media (min-width: 60rem) {
    .sc-editorial .sc-ed-title {
      font-size: clamp(2.375rem, min(4.2vw, 7.2vh), 3.75rem);
    }
    @supports (height: 100svh) {
      .sc-editorial .sc-ed-title { font-size: clamp(2.375rem, min(4.2vw, 7.2svh), 3.75rem); }
    }
  }

  /* Laptops bajas: se compacta el encabezado, nunca el texto del artículo. */
  @media (min-width: 60rem) and (max-height: 46.25rem) {
    .sc-editorial {
      --sc-space: 2rem;
      --sc-hero-space: 1.5rem;
      --sc-stack: 1rem;
      --sc-gap: 1.5rem;
    }
    .sc-editorial .sc-ed-title { font-size: clamp(2.25rem, min(4.2vw, 6.8vh), 3.25rem); }
    .sc-editorial .sc-ed-masthead { padding-block: .5rem; }
  }

  @media (max-height: 32rem) {
    .sc-editorial .sc-ed-aside { position: static; }
  }

  @media print {
    .sc-editorial .sc-ed-hero { color: var(--sc-ink); background: white; --sc-rule: #BBB; }
    .sc-editorial .sc-ed-orbits { display: none; }
    .sc-editorial .sc-ed-wrap { padding: 0; }
    .sc-editorial .sc-ed-header { padding-block: 1.5rem; }
    .sc-editorial .sc-ed-hero .sc-ed-title,
    .sc-editorial .sc-ed-hero .sc-ed-publication,
    .sc-editorial .sc-ed-hero .sc-ed-byline strong,
    .sc-editorial .sc-ed-hero .sc-ed-deck,
    .sc-editorial .sc-ed-hero .sc-ed-byline,
    .sc-editorial .sc-ed-hero .sc-ed-details,
    .sc-editorial .sc-ed-hero .sc-ed-edition,
    .sc-editorial .sc-ed-hero .sc-ed-kicker { color: var(--sc-ink); }
    .sc-editorial .sc-ed-cover img { max-height: none; }
    .sc-editorial .sc-ed-title { font-size: 26pt; }
    .sc-editorial .sc-ed-prose h2, .sc-ed-prose h3 { break-after: avoid; }
    .sc-editorial .sc-ed-prose p { orphans: 3; widows: 3; }
  }

`;
/* ======================================================
   IDIOMA
====================================================== */

const LANGUAGE_STORAGE_KEY = "solcont-language";
const LANGUAGE_CHANGE_EVENT = "solcont:language-change";

const LOCALES = {
  es: "es-MX",
  en: "en-US",
  fr: "fr-CA",
};

const OG_LOCALES = {
  es: "es_MX",
  en: "en_US",
  fr: "fr_CA",
};

function normalizeLanguage(value) {
  if (typeof value !== "string") return null;

  const code = value.trim().toLowerCase().split(/[-_]/)[0];
  return ["es", "en", "fr"].includes(code) ? code : null;
}

function readStoredLanguage() {
  if (typeof window === "undefined") return "es";

  try {
    return (
      normalizeLanguage(
        window.localStorage.getItem(LANGUAGE_STORAGE_KEY)
      ) || "es"
    );
  } catch {
    return "es";
  }
}

function useLanguage(controlledLanguage) {
  const [storedLanguage, setStoredLanguage] = useState(readStoredLanguage);

  useEffect(() => {
    function handleChange(event) {
      const value =
        typeof event.detail === "string"
          ? event.detail
          : event.detail?.language;

      const next = normalizeLanguage(value);
      if (!next) return;

      setStoredLanguage(next);

      try {
        window.localStorage.setItem(LANGUAGE_STORAGE_KEY, next);
      } catch {
        // El cambio sigue funcionando sin almacenamiento.
      }
    }

    function handleStorage(event) {
      if (event.key === LANGUAGE_STORAGE_KEY || event.key === null) {
        setStoredLanguage(readStoredLanguage());
      }
    }

    window.addEventListener(LANGUAGE_CHANGE_EVENT, handleChange);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(LANGUAGE_CHANGE_EVENT, handleChange);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  return normalizeLanguage(controlledLanguage) || storedLanguage;
}

/* ======================================================
   TEXTOS
====================================================== */

const CONTENT = {
  es: {
    home: "Inicio",
    blog: "Blog",
    navigation: "Ruta de navegación",
    edition: "Contabilidad + estrategia fiscal",
    section: "Ideas y análisis",
    reading: "min de lectura",
    share: "Compartir",
    shareOn: "Compartir en",
    newWindow: "abre una nueva pestaña",
    writtenBy: "Por",
    previous: "Artículo anterior",
    next: "Siguiente artículo",
    articleNavigation: "Navegación entre artículos",
    back: "Volver a Visión Contable",
    untitled: "Artículo sin título",
    originalNote:
      "Estás leyendo el artículo en su idioma original. El selector de idioma modifica únicamente la interfaz.",
    contentLanguage: "Idioma del artículo",
    languageNames: {
      es: "Español",
      en: "English",
      fr: "Français",
    },
    fallbackDescription:
      "Un artículo de Visión Contable sobre contabilidad, negocios y estrategia fiscal en México.",
    unavailable: "El contenido de este artículo todavía no está disponible.",
    notFoundTitle: "No encontramos este artículo.",
    notFoundDescription:
      "El enlace puede ser incorrecto o la publicación ya no estar disponible. Puedes consultar los demás artículos de Visión Contable.",
    notFoundSeo: "Artículo no encontrado | SolCont México",
  },

  en: {
    home: "Home",
    blog: "Blog",
    navigation: "Breadcrumb navigation",
    edition: "Accounting + tax strategy",
    section: "Ideas and analysis",
    reading: "min read",
    share: "Share",
    shareOn: "Share on",
    newWindow: "opens a new tab",
    writtenBy: "By",
    previous: "Previous article",
    next: "Next article",
    articleNavigation: "Article navigation",
    back: "Back to Visión Contable",
    untitled: "Untitled article",
    originalNote:
      "You are reading the article in its original language. The language selector changes the interface only.",
    contentLanguage: "Article language",
    languageNames: {
      es: "Español",
      en: "English",
      fr: "Français",
    },
    fallbackDescription:
      "A Visión Contable article about accounting, business and tax strategy in Mexico.",
    unavailable: "This article’s content is not available yet.",
    notFoundTitle: "We couldn’t find this article.",
    notFoundDescription:
      "The link may be incorrect, or the publication may no longer be available. You can explore the other articles in Visión Contable.",
    notFoundSeo: "Article not found | SolCont México",
  },

  fr: {
    home: "Accueil",
    blog: "Blog",
    navigation: "Fil d’Ariane",
    edition: "Comptabilité + stratégie fiscale",
    section: "Idées et analyses",
    reading: "min de lecture",
    share: "Partager",
    shareOn: "Partager sur",
    newWindow: "ouvre un nouvel onglet",
    writtenBy: "Par",
    previous: "Article précédent",
    next: "Article suivant",
    articleNavigation: "Navigation entre les articles",
    back: "Retour à Visión Contable",
    untitled: "Article sans titre",
    originalNote:
      "Vous lisez l’article dans sa langue d’origine. Le sélecteur de langue modifie uniquement l’interface.",
    contentLanguage: "Langue de l’article",
    languageNames: {
      es: "Español",
      en: "English",
      fr: "Français",
    },
    fallbackDescription:
      "Un article de Visión Contable sur la comptabilité, les entreprises et la stratégie fiscale au Mexique.",
    unavailable: "Le contenu de cet article n’est pas encore disponible.",
    notFoundTitle: "Cet article est introuvable.",
    notFoundDescription:
      "Le lien est peut-être incorrect ou la publication n’est plus disponible. Vous pouvez consulter les autres articles de Visión Contable.",
    notFoundSeo: "Article introuvable | SolCont México",
  },
};

/* ======================================================
   DATOS
====================================================== */

function plainText(value) {
  if (typeof value !== "string" && typeof value !== "number") {
    return "";
  }

  return String(value)
    .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, " ")
    .replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, " ")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;|&#160;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/\s+/g, " ")
    .trim();
}

function getRawBody(post) {
  return Object.keys(post)
    .filter((key) => /^body\d+$/.test(key))
    .sort((a, b) => Number(a.slice(4)) - Number(b.slice(4)))
    .map((key) => post[key])
    .filter((value) => typeof value === "string" && value.trim())
    // Cada bloque tiene su propio espacio editorial.
    // Todo se sanea posteriormente con DOMPurify.
    .map((value) => `<div>${value}</div>`)
    .join("\n");
}

function estimateReadingTime(html) {
  const text = plainText(html);
  const words = text ? text.split(/\s+/).length : 0;

  return words ? Math.max(1, Math.ceil(words / 200)) : null;
}

function getContentLanguage(post) {
  const raw =
    typeof post.language === "string"
      ? post.language
      : typeof post.lang === "string"
      ? post.lang
      : "es";

  const code = raw.trim().replace(/_/g, "-").toLowerCase();

  return /^[a-z]{2,3}(?:-[a-z0-9]{2,8})*$/.test(code) ? code : "es";
}

function parsePostDate(value) {
  if (typeof value !== "string") return null;

  const raw = value.trim();
  if (!raw) return null;

  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const local = raw.match(/^(\d{2})[/-](\d{2})[/-](\d{4})$/);

  if (iso || local) {
    const year = Number(iso ? iso[1] : local[3]);
    const month = Number(iso ? iso[2] : local[2]);
    const day = Number(iso ? iso[3] : local[1]);

    const date = new Date(Date.UTC(year, month - 1, day));

    if (
      date.getUTCFullYear() !== year ||
      date.getUTCMonth() !== month - 1 ||
      date.getUTCDate() !== day
    ) {
      return null;
    }

    return date;
  }

  if (
    /^\d{4}-\d{2}-\d{2}T/.test(raw) &&
    /(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw)
  ) {
    const timestamp = Date.parse(raw);

    if (Number.isFinite(timestamp)) return new Date(timestamp);
  }

  return null;
}

function compareArticles(a, b) {
  if (a.timestamp !== null && b.timestamp !== null) {
    const difference = b.timestamp - a.timestamp;
    if (difference !== 0) return difference;
  } else if (a.timestamp !== null || b.timestamp !== null) {
    return a.timestamp !== null ? -1 : 1;
  }

  if (a.numericId !== null && b.numericId !== null) {
    const difference = b.numericId - a.numericId;
    if (difference !== 0) return difference;
  }

  return a.index - b.index;
}

function prepareArticles(data) {
  if (!Array.isArray(data)) return [];

  return data
    .filter(
      (post) =>
        post &&
        typeof post === "object" &&
        (typeof post.id === "string" || typeof post.id === "number") &&
        String(post.id).trim() !== ""
    )
    .map((post, index) => {
      const date = parsePostDate(post.date);
      const modifiedDate = parsePostDate(post.dateModified);
      const numericId = Number(post.id);

      return {
        id: post.id,
        index,
        title: plainText(post.title),
        description: plainText(post.description),
        author: plainText(post.autor),
        image: typeof post.image === "string" ? post.image.trim() : "",
        imageAlt: plainText(post.imageAlt),
        imageCaption: plainText(post.imageCaption),
        rawDate: plainText(post.date),
        date,
        modifiedDate,
        timestamp: date ? date.getTime() : null,
        numericId: Number.isFinite(numericId) ? numericId : null,
        contentLanguage: getContentLanguage(post),
        rawBody: getRawBody(post),
      };
    })
    .sort(compareArticles);
}

function absoluteHttpUrl(value, origin) {
  if (!value) return null;

  try {
    const url = new URL(value, `${origin}/`);

    return ["http:", "https:"].includes(url.protocol)
      ? url.href
      : null;
  } catch {
    return null;
  }
}

/* ======================================================
   HTML PERMITIDO
====================================================== */

const HTML_POLICY = {
  ALLOWED_TAGS: [
    "p", "br", "div", "span", "section",
    "h2", "h3", "h4", "h5", "h6",
    "ul", "ol", "li",
    "strong", "em", "b", "i", "u", "s",
    "blockquote", "a", "img", "figure", "figcaption",
    "table", "caption", "thead", "tbody", "tfoot",
    "tr", "th", "td",
    "pre", "code", "hr", "sup", "sub",
    "dl", "dt", "dd",
  ],
  ALLOWED_ATTR: [
    "href", "title", "src", "alt",
    "width", "height",
    "colspan", "rowspan", "scope",
    "start", "reversed",
    "lang", "dir",
  ],
};

/* ======================================================
   PANTALLA
====================================================== */

export default function BlogArticleScreen({
  language: controlledLanguage,
} = {}) {
  const { id } = useParams();
  const language = useLanguage(controlledLanguage);
  const t = CONTENT[language];

  const articles = useMemo(() => prepareArticles(blogData), []);

  const currentIndex = useMemo(
    () => articles.findIndex((post) => String(post.id) === String(id)),
    [articles, id]
  );

  const article = currentIndex >= 0 ? articles[currentIndex] : null;
  const previous = currentIndex > 0 ? articles[currentIndex - 1] : null;
  const next =
    currentIndex >= 0 && currentIndex < articles.length - 1
      ? articles[currentIndex + 1]
      : null;

  const rawBody = article?.rawBody || "";

  const safeHtml = useMemo(
    () => DOMPurify.sanitize(rawBody, HTML_POLICY),
    [rawBody]
  );

  const readingTime = useMemo(
    () => estimateReadingTime(safeHtml),
    [safeHtml]
  );

  const dateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(LOCALES[language], {
        year: "numeric",
        month: "long",
        day: "numeric",
        timeZone: "UTC",
      }),
    [language]
  );

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [id]);

  const origin =
    typeof window !== "undefined"
      ? window.location.origin
      : "https://solcont.com.mx";

  const canonical =
    typeof window !== "undefined"
      ? `${origin}${window.location.pathname}`
      : `${origin}/article/${encodeURIComponent(String(id || ""))}`;

  if (!article) {
    return (
      <div lang={language} className="sc-editorial">
        <style>{EDITORIAL_STYLES}</style>

        <Helmet>
          <title>{t.notFoundSeo}</title>
          <meta name="description" content={t.notFoundDescription} />
          <meta name="robots" content="noindex" />
        </Helmet>

        <div className="sc-ed-wrap">
          <PublicationMasthead t={t} />

          <section className="sc-ed-missing">
            <p className="sc-ed-missing-code">404</p>
            <h1 className="sc-ed-title">{t.notFoundTitle}</h1>
            <p>{t.notFoundDescription}</p>

            <Link to="/blog" className="sc-ed-back">
              <ArrowLeft size={16} aria-hidden="true" />
              {t.back}
            </Link>
          </section>
        </div>
      </div>
    );
  }

  const articleTitle = article.title || t.untitled;
  const sourceLanguage = article.contentLanguage.split("-")[0];
  const sourceTexts = CONTENT[sourceLanguage] || t;
  const languageName =
    t.languageNames[sourceLanguage] || article.contentLanguage;

  const pageTitle = `${articleTitle} | Visión Contable — SolCont México`;
  const pageDescription =
    article.description || sourceTexts.fallbackDescription;

  const imageUrl = absoluteHttpUrl(article.image, origin);
  const dateLabel = article.date
    ? dateFormatter.format(article.date)
    : article.rawDate;

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: articleTitle,
    description: pageDescription,
    url: canonical,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": canonical,
    },
    author: article.author
      ? { "@type": "Person", name: article.author }
      : { "@type": "Organization", name: "SolCont México" },
    publisher: {
      "@type": "Organization",
      name: "SolCont México",
      url: "https://solcont.com.mx",
    },
    inLanguage: article.contentLanguage,
    ...(imageUrl ? { image: [imageUrl] } : {}),
    ...(article.date
      ? { datePublished: article.date.toISOString() }
      : {}),
    ...(article.modifiedDate
      ? { dateModified: article.modifiedDate.toISOString() }
      : {}),
  };

  return (
    <div lang={language} className="sc-editorial">
      <style>{EDITORIAL_STYLES}</style>

      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={canonical} />

        <meta property="og:type" content="article" />
        <meta property="og:title" content={articleTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={canonical} />
        <meta property="og:site_name" content="SolCont México" />

        {OG_LOCALES[sourceLanguage] && (
          <meta
            property="og:locale"
            content={OG_LOCALES[sourceLanguage]}
          />
        )}

        {imageUrl && <meta property="og:image" content={imageUrl} />}

        {article.date && (
          <meta
            property="article:published_time"
            content={article.date.toISOString()}
          />
        )}

        {article.modifiedDate && (
          <meta
            property="article:modified_time"
            content={article.modifiedDate.toISOString()}
          />
        )}

        <meta
          name="twitter:card"
          content={imageUrl ? "summary_large_image" : "summary"}
        />
        <meta name="twitter:title" content={articleTitle} />
        <meta name="twitter:description" content={pageDescription} />

        {imageUrl && <meta name="twitter:image" content={imageUrl} />}

        <script type="application/ld+json">
          {JSON.stringify(structuredData).replace(/</g, "\\u003c")}
        </script>
      </Helmet>

      <article className="sc-ed-article">
        <div className="sc-ed-hero">
          <div aria-hidden="true" className="sc-ed-orbits">
            <span className="sc-ed-orbit" />
          </div>
          <div className="sc-ed-wrap sc-ed-hero-inner">
            {/* RUTA */}
            <nav aria-label={t.navigation} className="sc-ed-breadcrumbs">
              <ol>
                <li>
                  <Link to="/">{t.home}</Link>
                </li>

                <li aria-hidden="true">/</li>

                <li>
                  <Link to="/blog">{t.blog}</Link>
                </li>

                <li aria-hidden="true">/</li>

                <li
                  aria-current="page"
                  lang={article.title ? article.contentLanguage : undefined}
                >
                  {articleTitle}
                </li>
              </ol>
            </nav>

            {/* CABECERA DE PUBLICACIÓN */}
            <PublicationMasthead t={t} />

            {/* TITULAR Y BAJADA */}
            <header className="sc-ed-header">
              <p className="sc-ed-kicker">{t.section}</p>

              <h1
                className="sc-ed-title"
                lang={article.title ? article.contentLanguage : undefined}
              >
                {articleTitle}
              </h1>

              {article.description && (
                <p
                  className="sc-ed-deck"
                  lang={article.contentLanguage}
                >
                  {article.description}
                </p>
              )}

              <div className="sc-ed-meta">
                <p className="sc-ed-byline">
                  {t.writtenBy}
                  <strong lang={article.contentLanguage}>
                    {article.author || "SolCont México"}
                  </strong>
                </p>

                <div className="sc-ed-details">
                  {article.date ? (
                    <time dateTime={article.date.toISOString()}>
                      {dateLabel}
                    </time>
                  ) : dateLabel ? (
                    <span lang={article.contentLanguage}>
                      {dateLabel}
                    </span>
                  ) : null}

                  {readingTime !== null && (
                    <span>
                      {readingTime} {t.reading}
                    </span>
                  )}
                </div>
              </div>
            </header>
          </div>
        </div>

        <div className="sc-ed-wrap">
          {/* IMAGEN ORIGINAL, SIN RECORTE */}
          {imageUrl && (
            <EditorialCover
              src={imageUrl}
              alt={article.imageAlt}
              caption={article.imageCaption}
              language={article.contentLanguage}
            />
          )}

          {/* LECTURA Y COLUMNA LATERAL */}
          <div className="sc-ed-layout">
            <div className="sc-ed-reading">
              {sourceLanguage !== language && (
                <p className="sc-ed-language-note">
                  {t.originalNote}
                </p>
              )}

              {safeHtml.trim() ? (
                <div
                  lang={article.contentLanguage}
                  className="sc-ed-prose"
                  dangerouslySetInnerHTML={{ __html: safeHtml }}
                />
              ) : (
                <p className="sc-ed-language-note">
                  {t.unavailable}
                </p>
              )}

              <span aria-hidden="true" className="sc-ed-endmark" />

              <footer className="sc-ed-signature">
                <p>
                  {t.writtenBy}{" "}
                  <strong lang={article.contentLanguage}>
                    {article.author || "SolCont México"}
                  </strong>
                </p>
                <p>SolCont México · {t.edition}</p>
              </footer>
            </div>

            <aside className="sc-ed-aside" aria-label={t.share}>
              <p className="sc-ed-aside-title">{t.share}</p>

              <ShareLinks
                url={canonical}
                title={articleTitle}
                t={t}
              />

              <p className="sc-ed-aside-language">
                {t.contentLanguage}
                <br />
                <span lang={article.contentLanguage}>
                  {languageName}
                </span>
              </p>
            </aside>
          </div>
        </div>
      </article>

      <div className="sc-ed-wrap">
        {/* SIGUIENTES LECTURAS */}
        {(previous || next) && (
          <nav
            className="sc-ed-nav"
            aria-label={t.articleNavigation}
          >
            {previous && (
              <ArticleNavigation
                article={previous}
                direction="previous"
                t={t}
              />
            )}

            {next && (
              <ArticleNavigation
                article={next}
                direction="next"
                t={t}
              />
            )}
          </nav>
        )}

        <div className="sc-ed-back-wrap">
          <Link to="/blog" className="sc-ed-back">
            <ArrowLeft size={16} aria-hidden="true" />
            {t.back}
          </Link>
        </div>
      </div>
    </div>
  );
}

/* ======================================================
   CABECERA EDITORIAL
   Nombre de la publicación, no reconstrucción del logo.
====================================================== */

function PublicationMasthead({ t }) {
  return (
    <div className="sc-ed-masthead">
      <Link to="/blog" className="sc-ed-publication" lang="es">
        <span aria-hidden="true" className="sc-ed-dot" />
        VISIÓN CONTABLE
      </Link>

      <p className="sc-ed-edition">
        SolCont México
        <br />
        {t.edition}
      </p>
    </div>
  );
}

/* ======================================================
   IMAGEN
   Si falla, se omite sin añadir una ilustración ficticia.
====================================================== */

function EditorialCover({ src, alt, caption, language }) {
  const [failedSource, setFailedSource] = useState(null);

  if (failedSource === src) return null;

  return (
    <figure className="sc-ed-cover" lang={language}>
      <img
        src={src}
        alt={alt || ""}
        loading="eager"
        decoding="async"
        onError={() => setFailedSource(src)}
      />

      {caption && <figcaption>{caption}</figcaption>}
    </figure>
  );
}

/* ======================================================
   ANTERIOR / SIGUIENTE
====================================================== */

function ArticleNavigation({ article, direction, t }) {
  const isNext = direction === "next";

  return (
    <Link
      to={`/article/${article.id}`}
      className={`sc-ed-nav-link${isNext ? " is-next" : ""}`}
    >
      <span className="sc-ed-nav-label">
        {!isNext && (
          <ChevronLeft size={15} aria-hidden="true" />
        )}

        {isNext ? t.next : t.previous}

        {isNext && (
          <ChevronRight size={15} aria-hidden="true" />
        )}
      </span>

      <p
        className="sc-ed-nav-title"
        lang={article.title ? article.contentLanguage : undefined}
      >
        {article.title || t.untitled}
      </p>
    </Link>
  );
}

/* ======================================================
   COMPARTIR
====================================================== */

function ShareLinks({ url, title, t }) {
  const encode = encodeURIComponent;

  const links = [
    {
      label: "WhatsApp",
      href: `https://wa.me/?text=${encode(`${title} ${url}`)}`,
    },
    {
      label: "LinkedIn",
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encode(url)}`,
    },
    {
      label: "X",
      href: `https://twitter.com/intent/tweet?text=${encode(title)}&url=${encode(url)}`,
    },
  ];

  return (
    <div className="sc-ed-share">
      {links.map((link) => (
        <a
          key={link.label}
          href={link.href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`${t.shareOn} ${link.label} (${t.newWindow})`}
        >
          {link.label}
        </a>
      ))}
    </div>
  );
}
